import { Heading, VStack, Input, Button, Box, Table, Thead, Tbody, Tr, Th, Td, Select, useToast, Checkbox, Text, useColorModeValue, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Collapse, HStack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Badge } from '@chakra-ui/react';
// ===== [CATEGORY ACCORDION] Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Badge added to imports above =====
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// ===== [CATEGORY ACCORDION] useMemo added to React imports above =====
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import { format } from 'date-fns';
import { useNavigate } from "react-router-dom";
import { FiEdit, FiCheck, FiX, FiTrash, FiSettings } from "react-icons/fi";
import { QuestionSet, QuestionSetCategory } from '../../Platform/Common/types';

interface Tenant {
  tenant_id: number;
  name: string;
  description: string;
  is_active: boolean;
}

// Define the expected structure of the token payload
interface TokenPayload {
  sub: string;       // Typically the username or user ID
  role_id: number;   // Role ID
  tenant_id: number; // Tenant ID
  exp?: number;      // Expiration timestamp (optional)
}

// ===== [CATEGORY ACCORDION] key used to group question sets with no matching category =====
const UNCATEGORIZED_KEY = "uncategorized";

const B2CQsetManager = () => {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);

  const navigate = useNavigate();

  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editRow, setEditRow] = useState<number | null>(null); // Track the row currently being edited
  const [newQuestionSet, setNewQuestionSet] = useState<QuestionSet | null>(null);

  const [categories, setCategories] = useState<QuestionSetCategory[]>([]);

  // ===== [CATEGORY ACCORDION] pending category state START =====
  // Holds an in-progress category change while a row is being edited.
  // Kept separate from questionSet.category_id so the accordion grouping
  // (which reads category_id directly, only reflecting SAVED state) never
  // reacts until Update is actually clicked -- prevents the row jumping
  // accordions mid-edit, before the change is confirmed.
  const [pendingCategoryId, setPendingCategoryId] = useState<number | null>(null);
  // ===== [CATEGORY ACCORDION] pending category state END =====


const fetchQuestionSets = useCallback(async () => {
  try {
    const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${apiurl}/qsets`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setQuestionSets(response.data);
  } catch (error) {
    console.error("Failed to load question sets", error);
  }
}, []);

const fetchQuestionSetCategories = useCallback(async () => {
  try {
    const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${apiurl}/qset-categories`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setCategories(response.data);
  } catch (error) {
    console.error("Failed to load categories", error);
  }
}, []);

useEffect(() => {
  fetchQuestionSets();
  fetchQuestionSetCategories();
}, [fetchQuestionSets, fetchQuestionSetCategories]);

  // ===== [CATEGORY ACCORDION] grouping logic START =====
  // Groups the flat questionSets array by category_id, sorted by
  // category name/subject label (NOT category_id) so the accordion order
  // stays alphabetically stable regardless of when a category was created.
  // "Uncategorized" is always pinned last. This is purely a render-time
  // derivation -- category_id on each question set remains the single
  // source of truth; nothing here mutates questionSets or categories.
  const groupedByCategory = useMemo(() => {
    const groups = new Map<string, { label: string; items: QuestionSet[] }>();

    questionSets.forEach((qs) => {
      const category = categories.find((c) => c.category_id === qs.category_id);
      const key = category ? String(category.category_id) : UNCATEGORIZED_KEY;
      const label = category
        ? `${category.category_name} - ${category.subject}`
        : "Uncategorized";

      if (!groups.has(key)) {
        groups.set(key, { label, items: [] });
      }
      groups.get(key)!.items.push(qs);
    });

    const sorted = Array.from(groups.entries())
      .filter(([key]) => key !== UNCATEGORIZED_KEY)
      .sort((a, b) => a[1].label.localeCompare(b[1].label));

    const uncategorized = groups.get(UNCATEGORIZED_KEY);
    if (uncategorized) {
      sorted.push([UNCATEGORIZED_KEY, uncategorized]);
    }

    return sorted;
  }, [questionSets, categories]);
  // ===== [CATEGORY ACCORDION] grouping logic END =====

  const handleDeleteQuestionSet = async (question_set_id: number) => {
    // Placeholder function
       try {
         const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
       console.log("Printing Token from Delete" + localStorage.getItem("b2c_token"));
       await axios.delete(apiurl + `/qset/${question_set_id}`, {
         headers: {
           Authorization: `Bearer ${localStorage.getItem('b2c_token')}`
         }
       });
       fetchQuestionSets();
       setEditRow(null); // Reset edit mode after successful update
       toast({ title: "Question Set deleted successfully", status: "success" });
     } catch (err: any) {
      const msg = err.response?.data?.detail || "Failed to delete question set";
      toast({ title: msg, status: "error" });
    }
   
  };

   
  const handleReviewQuestionSet = (question_set_id: number) => {
    // Placeholder function

    const token = localStorage.getItem("b2c_token");
    if (token) {
      
    //  navigate(`/coaching/admin/dashboard/question-set-manager/review-question-set/${question_set_id}`);
       navigate(`/practice/app/b2c-qset-manager/review-b2c-qset/${question_set_id}`);
        }
    }

  const handleAddNewQuestionSet = () => {
    setIsAddingNew(true);
    setNewQuestionSet({ tenant_id:0, question_set_id: 0, category_id: 0, title: '',  
      is_active: true , description:'' , default_max_score:0, scoring_strategy:"BY_QUESTION", passing_percentage:0, 
      allow_negative:false, normalize_score:false, duration:0, created_at: new Date(), updated_at: new Date()});
  };

  const handleRemoveNewQuestionSet = () => {
    setIsAddingNew(false);
    setNewQuestionSet(null);
  };

  const handleSaveQuestionSet = async (questionset: QuestionSet) => {
    try {

        const payload = {
            title: questionset.title,
            description: questionset.description,
            default_max_score: questionset.default_max_score,
            scoring_strategy: questionset.scoring_strategy,
            allow_negative:questionset.allow_negative,
            normalize_score:questionset.normalize_score,
            passing_percentage :questionset.passing_percentage,
            duration: questionset.duration,
            category_id: questionset.category_id,
            created_at: questionset.created_at,
            updated_at: questionset.updated_at,
            tenant_id: questionset.tenant_id,
            is_active: questionset.is_active,
        };
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      
      console.log("printing questionset user payload",payload);

      const response = await axios.post(apiurl+`/create-qset/`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('b2c_token')}`
        }
      });
      setQuestionSets([...questionSets, response.data]);
      setIsAddingNew(false);
      setNewQuestionSet(null);
      toast({ title: "QuestionSet added successfully", status: "success" });
    } catch (error) {
      console.error("Failed to save QuestionSet ", error);
      toast({ title: "Failed to add QuestionSet ", status: "error" });
    }
  };

  const handleUpdateQuestionSet = async (questionset: QuestionSet) => {
    try {

        const payload = {
            title: questionset.title,
            description: questionset.description,
            default_max_score: questionset.default_max_score,
            scoring_strategy: questionset.scoring_strategy,
            allow_negative:questionset.allow_negative,
            normalize_score:questionset.normalize_score,
            passing_percentage :questionset.passing_percentage,
            duration: questionset.duration,
            // ===== [CATEGORY ACCORDION] category_id now resolved from pendingCategoryId START =====
            // Only the confirmed pending value (set if the dropdown was
            // touched during this edit session) overrides category_id --
            // otherwise the original, unmutated value is sent as-is.
            category_id: pendingCategoryId ?? questionset.category_id,
            // ===== [CATEGORY ACCORDION] category_id now resolved from pendingCategoryId END =====
            created_at: questionset.created_at,
            updated_at: questionset.updated_at,
            tenant_id:  questionset.tenant_id,
            is_active: questionset.is_active,
        };
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
     
      console.log("Printing Token from Update" + localStorage.getItem("b2c_token"));
      await axios.put(apiurl+`/update-qset/${questionset.question_set_id}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('b2c_token')}`
        }
      });
      fetchQuestionSets();
      setEditRow(null); // Reset edit mode after successful update
      setPendingCategoryId(null); // ===== [CATEGORY ACCORDION] clear pending value after successful save =====
      toast({ title: "QuestionSet updated successfully", status: "success" });
    } catch (error) {
      console.error("Failed to update QuestionSet", error);
      toast({ title: "Failed to update QuestionSet", status: "error" });
    }
  };

  const handleEditRow = (user_id: number) => {
    setEditRow(user_id);
    setIsAddingNew(false); // Disable add tenant while editing
    setPendingCategoryId(null); // ===== [CATEGORY ACCORDION] reset pending value on entering edit mode =====
  };

  const handleCancelEdit = () => {
    setEditRow(null);
    setIsAddingNew(false);
    setPendingCategoryId(null); // ===== [CATEGORY ACCORDION] discard any pending, unsaved category change =====
  };

  // ===== [CATEGORY ACCORDION] shared row renderer START =====
  // Identical <Tr> markup/behavior to the original flat-table row --
  // extracted into a function purely so it can be reused once per
  // category accordion instead of duplicated. No field-level logic
  // changed here except the category Select, marked below.
  const renderQuestionSetRow = (questionSet: QuestionSet) => (
    <Tr
      key={questionSet.question_set_id}
      opacity={editRow && editRow !== questionSet.question_set_id ? 0.5 : isAddingNew ? 0.5 : 1}
    >
      <Td>{questionSet.question_set_id}</Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Input
            size="sm"
            fontSize="sm"
            value={questionSet.title}
            onChange={(e) => {
              questionSet.title = e.target.value;
              setQuestionSets([...questionSets]);
            }}
          />
        ) : (
          questionSet.title
        )}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Input
            size="sm"
            fontSize="sm"
            value={questionSet.description}
            onChange={(e) => {
              questionSet.description = e.target.value;
              setQuestionSets([...questionSets]);
            }}
          />
        ) : (
          questionSet.description
        )}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Input
            size="sm"
            fontSize="sm"
            value={questionSet.default_max_score}
            onChange={(e) => {
              questionSet.default_max_score = Number(e.target.value);
              setQuestionSets([...questionSets]);
            }}
          />
        ) : (
          questionSet.default_max_score
        )}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Input
            size="sm"
            fontSize="sm"
            value={questionSet.passing_percentage}
            onChange={(e) => {
              questionSet.passing_percentage = Number(e.target.value);
              setQuestionSets([...questionSets]);
            }}
          />
        ) : (
          questionSet.passing_percentage
        )}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Select
            size="sm"
            fontSize="sm"
            value={questionSet.scoring_strategy}
            onChange={(e) => {
              questionSet.scoring_strategy = e.target.value as QuestionSet["scoring_strategy"];
              setQuestionSets([...questionSets]);
            }}
          >
            <option value="UNIFORM">Uniform</option>
            <option value="BY_QUESTION">By Question</option>
            <option value="WEIGHTED">Weighted</option>
          </Select>
        ) : (
          questionSet.scoring_strategy.replace(/_/g, " ")
        )}
      </Td>

      <Td>
        {editRow === questionSet.question_set_id ? (
          <Checkbox
            isChecked={questionSet.allow_negative}
            onChange={(e) => {
              questionSet.allow_negative = e.target.checked;
              setQuestionSets([...questionSets]);
            }}
          />
        ) : questionSet.allow_negative ? (
          "Yes"
        ) : (
          "No"
        )}
      </Td>

      <Td>
        {editRow === questionSet.question_set_id ? (
          <Checkbox
            isChecked={questionSet.normalize_score}
            onChange={(e) => {
              questionSet.normalize_score = e.target.checked;
              setQuestionSets([...questionSets]);
            }}
          />
        ) : questionSet.normalize_score ? (
          "Yes"
        ) : (
          "No"
        )}
      </Td>

      <Td>
        {editRow === questionSet.question_set_id ? (
          <Input
            size="sm"
            fontSize="sm"
            value={questionSet.duration}
            onChange={(e) => {
              questionSet.duration = Number(e.target.value);
              setQuestionSets([...questionSets]);
            }}
          />
        ) : (
          questionSet.duration
        )}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <Checkbox
            isChecked={questionSet.is_active}
            onChange={(e) => {
              questionSet.is_active = e.target.checked;
              setQuestionSets([...questionSets]);
            }}
          />
        ) : questionSet.is_active ? (
          "Active"
        ) : (
          "Inactive"
        )}
      </Td>
      <Td>
        {/* ===== [CATEGORY ACCORDION] category Select now reads/writes pendingCategoryId START ===== */}
        {editRow === questionSet.question_set_id ? (
          <Select
            size="sm"
            fontSize="sm"
            value={pendingCategoryId ?? questionSet.category_id ?? ""}
            onChange={(e) => setPendingCategoryId(Number(e.target.value))}
            placeholder="Select Category"
          >
            {categories.map((category) => (
              <option key={category.category_id} value={category.category_id}>
                {`${category.category_name} - ${category.subject}`}
              </option>
            ))}
          </Select>
        ) : (
          categories.find((c) => c.category_id === questionSet.category_id)
            ? `${categories.find((c) => c.category_id === questionSet.category_id)?.category_name} - ${categories.find((c) => c.category_id === questionSet.category_id)?.subject}`
            : "Uncategorized"
        )}
        {/* ===== [CATEGORY ACCORDION] category Select now reads/writes pendingCategoryId END ===== */}
      </Td>
      <Td>
        {editRow === questionSet.question_set_id ? (
          <HStack>
            <Tooltip label="Update">
              <Button size="sm" onClick={() => handleUpdateQuestionSet(questionSet)}>
                <FiCheck />
              </Button>
            </Tooltip>
            <Tooltip label="Cancel">
              <Button size="sm" ml={2} onClick={handleCancelEdit}>
                <FiX />
              </Button>
            </Tooltip>
          </HStack>
        ) : (
          <>
            <Tooltip label="Edit">
              <Button size="sm" onClick={() => handleEditRow(questionSet.question_set_id)} mr={2}>
                <FiEdit />
              </Button>
            </Tooltip>
            <Tooltip label="Delete">
              <Button
                size="sm"
                colorScheme="red"
                mr={2}
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this Question Set?")) {
                    handleDeleteQuestionSet(questionSet.question_set_id);
                  }
                }}
              >
                <FiTrash />
              </Button>
            </Tooltip>
            <Tooltip label="Review Question Set">
              <Button size="sm" onClick={() => handleReviewQuestionSet(questionSet.question_set_id)}>
                <FiSettings />
              </Button>
            </Tooltip>
          </>
        )}
      </Td>
    </Tr>
  );
  // ===== [CATEGORY ACCORDION] shared row renderer END =====

  // ===== [CATEGORY ACCORDION] shared table header, unchanged column set =====
  const tableHeader = (
    <Thead fontSize="12">
      <Tr bgColor={tableHeaderbg}>
        <Th>ID</Th>
        <Th>Title</Th>
        <Th>Description</Th>
        <Th>Default Max Score</Th>
        <Th>Passing %</Th>
        <Th>Scoring Strategy</Th>
        <Th>Allow Negative?</Th>
        <Th>Normalize Score?</Th>
        <Th>Duration</Th>
        <Th>Status</Th>
        <Th>Category</Th>
        <Th>Actions</Th>
      </Tr>
    </Thead>
  );

  return (
    <Box as="main" mx="auto" p={10} >
      <VStack>
        <Heading size={"md"}>Question Set Manager</Heading>
      </VStack>
            <Box mt={4}>
               <Text fontWeight="bold">Existing Question Set Categories</Text>
        <Button  mt={2} borderRadius={"sm"} fontSize="sm" fontWeight="normal" onClick={handleAddNewQuestionSet} isDisabled={isAddingNew || editRow !== null}>
          + Question Set
        </Button>
      </Box>

      {/* ===== [CATEGORY ACCORDION] "Add new" row extracted into its own standalone table START ===== */}
      {/* A brand-new question set has no confirmed category until saved,
          so it doesn't belong inside any category's accordion panel --
          this block is functionally identical to the original inline
          "isAddingNew" <Tr>, just moved into its own table above the
          accordions instead of living inside the old single flat table. */}
      {isAddingNew && newQuestionSet && (
        <Box mt={4}>
          <Table borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
            {tableHeader}
            <Tbody fontSize="12">
              <Tr>
                <Td>New</Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.title}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, title: e.target.value })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.description}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, description: e.target.value })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.default_max_score}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, default_max_score: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.passing_percentage}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, passing_percentage: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                  <Select
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.scoring_strategy}
                    onChange={(e) =>
                      setNewQuestionSet({
                        ...newQuestionSet,
                        scoring_strategy: e.target.value as QuestionSet["scoring_strategy"],
                      })
                    }
                    placeholder="Select Strategy"
                  >
                    <option value="UNIFORM">Uniform</option>
                    <option value="BY_QUESTION">By Question</option>
                    <option value="WEIGHTED">Weighted</option>
                  </Select>
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.allow_negative}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, allow_negative: e.target.checked })
                    }
                    />
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.normalize_score}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, normalize_score: e.target.checked })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.duration}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, duration: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.is_active}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, is_active: e.target.checked })
                    }
                    />
                </Td>

                <Td>
                    <Select
                        size="sm"
                        fontSize="sm"
                        value={newQuestionSet.category_id ?? ""}
                        onChange={(e) => {
                            setNewQuestionSet({
                                ...newQuestionSet,
                                category_id: Number(e.target.value),
                            });
                        }}
                        placeholder="Select Category"
                    >
                        {categories.map((category) => (
                            <option key={category.category_id} value={category.category_id}>
                                {`${category.category_name} - ${category.subject}`}
                            </option>
                        ))}
                    </Select>
                </Td>

                <Td>
                <HStack>
                    <Tooltip label="Save">
                    <Button size="sm" onClick={() => handleSaveQuestionSet(newQuestionSet)}>
                        <FiCheck />
                    </Button>
                    </Tooltip>
                    <Tooltip label="Cancel">
                    <Button size="sm" ml={2} onClick={handleRemoveNewQuestionSet}>
                        <FiX />
                    </Button>
                    </Tooltip>
                    </HStack>
                </Td>
                </Tr>
            </Tbody>
          </Table>
        </Box>
      )}
      {/* ===== [CATEGORY ACCORDION] "Add new" row extracted into its own standalone table END ===== */}

      {/* ===== [CATEGORY ACCORDION] existing question sets now grouped into per-category accordions START ===== */}
      {/* Replaces the original single flat <Table> that listed every
          question set in one long body. Row markup/behavior (via
          renderQuestionSetRow) and all handlers are otherwise identical
          to the original -- only the grouping/wrapping is new. */}
      <Box mt={6}>
        <Accordion allowMultiple defaultIndex={[]}>
          {groupedByCategory.map(([key, group]) => (
            <AccordionItem key={key} border="1px solid" borderColor="gray.200" borderRadius="sm" mb={3}>
              <AccordionButton py={3}>
                <HStack flex="1" spacing={3}>
                  <Text fontWeight="semibold" fontSize="sm">{group.label}</Text>
                  <Badge colorScheme="blue" fontSize="0.7em">{group.items.length}</Badge>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} overflowX="auto">
                <Table borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
                  {tableHeader}
                  <Tbody fontSize="12">
                    {group.items.map((questionSet) => renderQuestionSetRow(questionSet))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
      {/* ===== [CATEGORY ACCORDION] existing question sets now grouped into per-category accordions END ===== */}

    </Box>
  );
};

export default B2CQsetManager;